// ============================================================================
// ObjectivesManager — objectifs d'une equipe (E1b, F-225..F-231 / M-203..M-207)
// ============================================================================
// Portage du PoC components/equipes/objectives-manager.tsx : lignes en lecture au
// repos, statut cyclable au clic (optimiste + rollback), edition en place, ajout,
// suppression confirmee en deux temps dans la ligne. En lecture seule (!canWrite),
// memes lignes sans controles, statut en badge (F-229). Statuses VERBATIM :
// pending / in_progress / done ; cycle UI ferme (M-203). L'AUTORITE est SERVEUR
// (canWrite = affordance renvoyee par l'API — garde re-verifiee route, M-204).
// Tokens @gs/ui uniquement (regle #28) — btnAction/btnSecondary/btnGhost/GsIconButton.

import { useState } from 'react';
import {
  Calendar, CheckCircle2, Circle, CircleDot, Pencil, Plus, Save, Target, Trash2, X,
} from 'lucide-react';
import {
  EmptyState, StatusBadge, GsProgressBar, GsIconButton, GS_COMPONENTS,
  type StatusBadgeProps,
} from '@gs/ui';
import { useTranslation } from '../../i18n';
import { formatDateOnly } from '../../pages/equipes/dates';
import {
  useCreateObjective, useUpdateObjective, useDeleteObjective,
  type ObjectiveStatus, type TeamObjective,
} from '../../hooks/useTeams';

const INPUT_DARK = 'dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100';

const STATUS_META: Record<ObjectiveStatus, { icon: typeof Circle; iconClass: string; badge: StatusBadgeProps['variant'] }> = {
  pending:     { icon: Circle,       iconClass: 'text-slate-400',   badge: 'neutral' },
  in_progress: { icon: CircleDot,    iconClass: 'text-blue-600 dark:text-blue-400', badge: 'info' },
  done:        { icon: CheckCircle2, iconClass: 'text-green-600 dark:text-green-400', badge: 'success' },
};

const NEXT_STATUS: Record<ObjectiveStatus, ObjectiveStatus> = {
  pending: 'in_progress',
  in_progress: 'done',
  done: 'pending',
};

const STATUSES: ObjectiveStatus[] = ['pending', 'in_progress', 'done'];

type ObjectiveFields = Omit<TeamObjective, 'id'>;

/** Non termines d'abord (echeance croissante, sans echeance en dernier), puis termines. */
function sortObjectives(list: TeamObjective[]): TeamObjective[] {
  const key = (o: TeamObjective) =>
    `${o.status === 'done' ? 1 : 0}|${o.targetDate ?? '9999-12-31'}|${o.title}`;
  return [...list].sort((a, b) => key(a).localeCompare(key(b), 'fr'));
}

/** 'YYYY-MM-DD' local (comparaison retard — pas d'UTC pour eviter le decalage de fuseau). */
function todayString(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function ObjectivesManager({
  teamId,
  year,
  initial,
  canWrite,
}: {
  teamId: string;
  year: number;
  initial: TeamObjective[];
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const create = useCreateObjective(teamId);
  const update = useUpdateObjective(teamId);
  const remove = useDeleteObjective(teamId);

  const [items, setItems] = useState<TeamObjective[]>(() => sortObjectives(initial));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = todayString();
  const doneCount = items.filter((o) => o.status === 'done').length;
  const busy = create.isPending || update.isPending || remove.isPending;

  function statusLabel(status: ObjectiveStatus): string {
    return t(`gest:equipes.objectives.status.${status}`);
  }

  // F-226 : cycle de statut optimiste + rollback (M-203, cycle ferme).
  function handleCycle(obj: TeamObjective) {
    const next = NEXT_STATUS[obj.status];
    const prev = items;
    setItems(sortObjectives(items.map((o) => (o.id === obj.id ? { ...o, status: next } : o))));
    setError(null);
    update.mutate(
      { id: obj.id, payload: { title: obj.title, status: next, targetDate: obj.targetDate } },
      { onError: () => { setItems(prev); setError(t('gest:equipes.objectives.cycleError')); } },
    );
  }

  // F-227 : edition en place (pessimiste).
  function handleSave(id: string, fields: ObjectiveFields) {
    setError(null);
    update.mutate(
      { id, payload: { title: fields.title, status: fields.status, targetDate: fields.targetDate } },
      {
        onSuccess: () => {
          setItems((cur) => sortObjectives(cur.map((o) => (o.id === id ? { ...o, ...fields } : o))));
          setEditingId(null);
        },
        onError: () => setError(t('gest:equipes.objectives.saveError')),
      },
    );
  }

  // F-225 : ajout (creation cote serveur, year force M-206).
  function handleAdd(fields: ObjectiveFields) {
    setError(null);
    create.mutate(fields, {
      onSuccess: (res) => {
        setItems((cur) => sortObjectives([...cur, { id: res.id, ...fields }]));
        setAddOpen(false);
      },
      onError: () => setError(t('gest:equipes.objectives.addError')),
    });
  }

  // F-228 : suppression optimiste + rollback + audit serveur.
  function handleDelete(id: string) {
    const prev = items;
    setItems(items.filter((o) => o.id !== id));
    setConfirmingId(null);
    setError(null);
    remove.mutate(id, {
      onError: () => { setItems(prev); setError(t('gest:equipes.objectives.deleteError')); },
    });
  }

  return (
    <section className="space-y-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">
          {t('gest:equipes.objectives.title', { year })}
        </h3>
        <div className="flex items-center gap-3">
          {items.length > 0 ? (
            <GsProgressBar value={doneCount} max={items.length} label className="w-36" />
          ) : null}
          {canWrite ? (
            <button
              type="button"
              aria-expanded={addOpen}
              onClick={() => setAddOpen((v) => !v)}
              className={`${addOpen ? GS_COMPONENTS.btnSecondary : GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
            >
              <Plus size={16} aria-hidden="true" />
              {t('gest:equipes.objectives.add')}
            </button>
          ) : null}
        </div>
      </header>

      {error ? (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {canWrite && addOpen ? (
        <ObjectiveEditForm
          idPrefix="new-obj"
          pending={create.isPending}
          submitLabel={t('gest:equipes.objectives.add')}
          onSubmit={handleAdd}
          onCancel={() => setAddOpen(false)}
        />
      ) : null}

      {items.length === 0 ? (
        addOpen ? null : (
          <EmptyState
            icon={<Target size={48} aria-hidden="true" />}
            title={t('gest:equipes.objectives.empty')}
            action={
              canWrite ? (
                <button type="button" onClick={() => setAddOpen(true)} className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}>
                  <Plus size={16} aria-hidden="true" />
                  {t('gest:equipes.objectives.add')}
                </button>
              ) : undefined
            }
          />
        )
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((o) =>
            editingId === o.id ? (
              <li key={o.id} className={`${GS_COMPONENTS.card} p-3.5 dark:border-slate-700 dark:bg-slate-800`}>
                <ObjectiveEditForm
                  idPrefix={`obj-${o.id}`}
                  initial={o}
                  pending={update.isPending}
                  submitLabel={t('gest:equipes.objectives.save')}
                  onSubmit={(fields) => handleSave(o.id, fields)}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            ) : (
              <ObjectiveDisplayRow
                key={o.id}
                obj={o}
                canWrite={canWrite}
                busy={busy}
                statusLabel={statusLabel}
                overdue={o.status !== 'done' && o.targetDate !== null && o.targetDate < today}
                confirming={confirmingId === o.id}
                onCycle={handleCycle}
                onEdit={(id) => { setConfirmingId(null); setEditingId(id); }}
                onAskDelete={setConfirmingId}
                onCancelDelete={() => setConfirmingId(null)}
                onConfirmDelete={handleDelete}
              />
            ),
          )}
        </ul>
      )}
    </section>
  );
}

// ─── Ligne d'affichage (lecture / controles) ─────────────────────────────────

function ObjectiveDisplayRow({
  obj, canWrite, busy, overdue, confirming, statusLabel,
  onCycle, onEdit, onAskDelete, onCancelDelete, onConfirmDelete,
}: {
  obj: TeamObjective;
  canWrite: boolean;
  busy: boolean;
  overdue: boolean;
  confirming: boolean;
  statusLabel: (s: ObjectiveStatus) => string;
  onCycle: (obj: TeamObjective) => void;
  onEdit: (id: string) => void;
  onAskDelete: (id: string) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const meta = STATUS_META[obj.status];
  const StatusIcon = meta.icon;
  const nextLabel = statusLabel(NEXT_STATUS[obj.status]);

  return (
    <li className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
      {canWrite ? (
        <GsIconButton
          icon={<StatusIcon size={18} className={meta.iconClass} aria-hidden="true" />}
          tooltip={t('gest:equipes.objectives.cycleTo', { status: nextLabel })}
          aria-label={t('gest:equipes.objectives.cycleFrom', { status: statusLabel(obj.status), next: nextLabel })}
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => onCycle(obj)}
        />
      ) : (
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center" aria-label={statusLabel(obj.status)}>
          <StatusIcon size={18} className={meta.iconClass} aria-hidden="true" />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className={`truncate text-field ${obj.status === 'done' ? 'text-slate-400 line-through dark:text-slate-500' : 'text-slate-800 dark:text-slate-100'}`}>
          {obj.title}
        </p>
        {obj.targetDate ? (
          <span className="mt-0.5 inline-flex items-center gap-1.5 text-meta text-slate-500 dark:text-slate-400">
            <Calendar size={13} aria-hidden="true" />
            {formatDateOnly(i18n.language, obj.targetDate)}
            {overdue ? <StatusBadge variant="warning">{t('gest:equipes.objectives.overdue')}</StatusBadge> : null}
          </span>
        ) : null}
      </div>

      {canWrite ? (
        confirming ? (
          <div className="flex shrink-0 items-center gap-1.5">
            <span className="text-meta text-slate-500 dark:text-slate-400">{t('gest:equipes.objectives.confirmDelete')}</span>
            <button type="button" disabled={busy} onClick={() => onConfirmDelete(obj.id)} className={GS_COMPONENTS.btnDanger}>
              {t('gest:equipes.objectives.confirm')}
            </button>
            <button type="button" onClick={onCancelDelete} className={GS_COMPONENTS.btnGhost}>
              {t('gest:equipes.objectives.cancel')}
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-0.5">
            <GsIconButton
              icon={<Pencil size={15} aria-hidden="true" />}
              tooltip={t('gest:equipes.objectives.edit')}
              aria-label={t('gest:equipes.objectives.editNamed', { title: obj.title })}
              size="sm" variant="ghost" disabled={busy}
              onClick={() => onEdit(obj.id)}
            />
            <GsIconButton
              icon={<Trash2 size={15} aria-hidden="true" />}
              tooltip={t('gest:equipes.objectives.delete')}
              aria-label={t('gest:equipes.objectives.deleteNamed', { title: obj.title })}
              size="sm" variant="ghost" disabled={busy}
              onClick={() => onAskDelete(obj.id)}
            />
          </div>
        )
      ) : (
        <StatusBadge variant={meta.badge}>{statusLabel(obj.status)}</StatusBadge>
      )}
    </li>
  );
}

// ─── Formulaire (ajout / edition) ────────────────────────────────────────────

function ObjectiveEditForm({
  idPrefix, initial, pending, submitLabel, onSubmit, onCancel,
}: {
  idPrefix: string;
  initial?: TeamObjective;
  pending: boolean;
  submitLabel: string;
  onSubmit: (fields: ObjectiveFields) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [status, setStatus] = useState<ObjectiveStatus>(initial?.status ?? 'pending');
  const [date, setDate] = useState(initial?.targetDate ?? '');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        onSubmit({ title: title.trim(), status, targetDate: date || null });
      }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}
      className="flex flex-wrap items-end gap-3"
    >
      <div className="flex min-w-40 flex-1 flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-title`} className="text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:equipes.objectives.fieldTitle')}
        </label>
        <input
          id={`${idPrefix}-title`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          maxLength={200}
          className={`${GS_COMPONENTS.input} ${INPUT_DARK}`}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-status`} className="text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:equipes.objectives.fieldStatus')}
        </label>
        <select
          id={`${idPrefix}-status`}
          value={status}
          onChange={(e) => setStatus(e.target.value as ObjectiveStatus)}
          className={`${GS_COMPONENTS.input} ${INPUT_DARK} w-40`}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{t(`gest:equipes.objectives.status.${s}`)}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idPrefix}-date`} className="text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:equipes.objectives.fieldDate')}
        </label>
        <input
          id={`${idPrefix}-date`}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={`${GS_COMPONENTS.input} ${INPUT_DARK} w-44`}
        />
      </div>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={pending || !title.trim()} className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}>
          <Save size={16} aria-hidden="true" />
          {submitLabel}
        </button>
        <button type="button" disabled={pending} onClick={onCancel} className={`${GS_COMPONENTS.btnGhost} inline-flex items-center gap-1.5`}>
          <X size={16} aria-hidden="true" />
          {t('gest:equipes.objectives.cancel')}
        </button>
      </div>
    </form>
  );
}
